import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * Shared shell + typography for long-form legal documents (Terms, Privacy,
 * Security). Simple, readable, no gimmicks.
 */
export function LegalPage({
  title,
  updatedOn,
  children,
}: {
  title: string;
  updatedOn: string;
  children: ReactNode;
}) {
  return (
    <div className="w-full bg-white">
      <section className="border-b border-[var(--ink-200)] bg-[var(--ink-50)]">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 pt-16 pb-10 md:pt-20 md:pb-14">
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
            Legal
          </div>
          <h1
            className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.02em]"
            style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.08 }}
          >
            {title}
          </h1>
          <p className="mt-4 text-sm text-[var(--ink-500)]">
            Last updated {updatedOn}
          </p>
          <div className="mt-6 flex gap-2 text-sm">
            <Link
              to="/terms"
              className="px-3 py-1 rounded-full border border-[var(--ink-200)] bg-white text-[var(--ink-700)] hover:text-[var(--ink-900)]"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="px-3 py-1 rounded-full border border-[var(--ink-200)] bg-white text-[var(--ink-700)] hover:text-[var(--ink-900)]"
            >
              Privacy
            </Link>
          </div>
        </div>
      </section>

      <article className="max-w-[820px] mx-auto px-5 md:px-8 py-16 legal-prose">
        {children}
      </article>

      <style>{`
        .legal-prose { color: var(--ink-700); font-size: 16px; line-height: 1.7; }
        .legal-prose p { margin: 0 0 1.1em 0; }
        .legal-prose h2 { font-family: var(--font-display), 'Inter', sans-serif; color: var(--ink-950); font-size: 24px; line-height: 1.25; font-weight: 600; letter-spacing: -0.01em; margin: 2.2em 0 0.75em 0; }
        .legal-prose h3 { color: var(--ink-900); font-size: 17px; line-height: 1.35; font-weight: 600; margin: 1.6em 0 0.5em 0; }
        .legal-prose ul { margin: 0 0 1.1em 1.25em; padding: 0; list-style: disc; }
        .legal-prose li { margin: 0.25em 0; padding-left: 0.25em; }
        .legal-prose a { color: var(--brand-700); text-decoration: underline; text-underline-offset: 2px; }
        .legal-prose a:hover { color: var(--brand-600); }
        .legal-prose strong { color: var(--ink-950); font-weight: 600; }
      `}</style>
    </div>
  );
}

export function Section({
  title,
  id,
  children,
}: {
  title: string;
  id?: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 id={id}>{title}</h2>
      {children}
    </section>
  );
}

export function SubSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <>
      <h3>{title}</h3>
      {children}
    </>
  );
}
